import { useState, useEffect, useCallback, useRef } from "react";
import { Building2, Plus, Loader2, X, Phone, LinkIcon, FileText, Trash2, Edit, Search, Upload, ExternalLink, Pin, PinOff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import { toast } from "sonner";

const PUBLIC_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const DEPARTMENTS = [
    { key: "verkauf", label: "Verkauf" },
    { key: "kundendienst", label: "Kundendienst" },
    { key: "teile_zubehoer", label: "Teile / Zubehör" },
    { key: "werkstatt", label: "Werkstatt" },
    { key: "disposition", label: "Disposition" },
    { key: "buchhaltung", label: "Buchhaltung" },
    { key: "sonstiges", label: "Sonstiges" },
];
const deptLabel = (k) => DEPARTMENTS.find((d) => d.key === k)?.label || k;

// Jul-2026 (User-Wunsch) — Start-Abteilung passend zur Mitarbeiter-Tätigkeit.
const FUNCTION_DEPT = {
    verkaeufer: "verkauf", verkauf: "verkauf",
    kundendienst: "kundendienst",
    mechaniker: "werkstatt", werkstatt: "werkstatt",
    teile: "teile_zubehoer", teile_zubehoer: "teile_zubehoer",
    buchhaltung: "buchhaltung",
    disposition: "disposition",
};

const emptyEntry = (department) => ({ department: department || "verkauf", title: "", info: "", phones: [], links: [], downloads: [], access_notes: "" });

export default function IntranetPage() {
    const { user } = useAuth();
    const [entries, setEntries] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    // Jul-2026 (User-Wunsch) — beim Öffnen mit der zuletzt genutzten Abteilung
    // starten; sobald der User geladen ist, gewinnt die Tätigkeits-Abteilung.
    const [activeDept, setActiveDeptRaw] = useState(() => {
        try { return localStorage.getItem("tw_intranet_dept") ?? ""; } catch { return ""; }
    });
    const deptInitRef = useRef(false);
    useEffect(() => {
        if (deptInitRef.current || !user) return;
        deptInitRef.current = true;
        const fnDept = FUNCTION_DEPT[(user.employee_function || "").toLowerCase()];
        if (fnDept) setActiveDeptRaw(fnDept);
    }, [user]);
    const setActiveDept = (d) => {
        setActiveDeptRaw(d);
        try { localStorage.setItem("tw_intranet_dept", d); } catch { /* noop */ }
    };
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // entry object or null

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (activeDept) params.department = activeDept;
            if (search.trim()) params.q = search.trim();
            const { data } = await api.get("/intranet", { params });
            setEntries(data.entries || []);
            setCounts(data.counts || {});
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [activeDept, search]);
    useEffect(() => { load(); }, [load]);

    const totalCount = Object.values(counts).reduce((s, n) => s + n, 0);

    const remove = async (id) => {
        if (!window.confirm("Eintrag wirklich löschen?")) return;
        try { await api.delete(`/intranet/${id}`); toast.success("Eintrag gelöscht"); await load(); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    const togglePin = async (e) => {
        try {
            await api.post(`/intranet/${e.id}/pin`, { pinned: !e.pinned });
            toast.success(e.pinned ? "Anheftung gelöst" : "Eintrag angeheftet");
            await load();
        } catch { toast.error("Aktion fehlgeschlagen"); }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8" data-testid="intranet-page">
            <EmployeeContextBanner />
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <Building2 className="w-7 h-7 text-[#FFCC00]" />
                <div className="flex-1 min-w-0">
                    <h1 className="font-display text-2xl font-black">Intranet</h1>
                    <p className="text-sm text-gray-500">Interne Informationen, Rufnummern, Links & Downloads — nach Abteilung.</p>
                </div>
                <button onClick={() => setEditing(emptyEntry(activeDept))} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold" data-testid="intranet-new-btn">
                    <Plus className="w-4 h-4" /> Neuer Eintrag
                </button>
            </div>

            <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-6 lg:items-start">
                {/* Abteilungs-Filter */}
                <aside className="mb-4 lg:mb-0" data-testid="intranet-departments">
                    <button onClick={() => setActiveDept("")} className={`w-full text-left px-3 py-2 rounded-sm text-sm font-bold flex items-center justify-between mb-1 ${activeDept === "" ? "bg-[#111827] text-white" : "hover:bg-gray-100"}`} data-testid="intranet-dept-all">
                        Alle Abteilungen <span className="text-[10px] opacity-70">{totalCount}</span>
                    </button>
                    {DEPARTMENTS.map((d) => (
                        <button key={d.key} onClick={() => setActiveDept(d.key)} className={`w-full text-left px-3 py-2 rounded-sm text-sm font-bold flex items-center justify-between mb-1 ${activeDept === d.key ? "bg-[#111827] text-white" : "hover:bg-gray-100"}`} data-testid={`intranet-dept-${d.key}`}>
                            {d.label} <span className="text-[10px] opacity-70">{counts[d.key] || 0}</span>
                        </button>
                    ))}
                </aside>

                <div className="min-w-0">
                    <div className="relative mb-4">
                        <Search className="w-4 h-4 text-emerald-600 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen …" className="w-full h-10 pl-9 pr-3 rounded-sm border-2 border-emerald-500 text-sm focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-400 placeholder:text-emerald-700/70" data-testid="intranet-search" />
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
                    ) : entries.length === 0 ? (
                        <div className="bg-white border border-dashed border-gray-300 rounded-sm py-16 text-center text-sm text-gray-400" data-testid="intranet-empty">
                            Noch keine Einträge{activeDept ? ` in „${deptLabel(activeDept)}"` : ""}. Legen Sie den ersten an.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="intranet-entries">
                            {entries.map((e) => (
                                <div key={e.id} className={`bg-white border rounded-sm p-4 ${e.pinned ? "border-[#FFCC00] ring-1 ring-[#FFCC00] shadow-sm" : "border-gray-200"}`} data-testid={`intranet-entry-${e.id}`}>
                                    <div className="flex items-start justify-between gap-2 mb-1.5">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                {e.pinned && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827] inline-flex items-center gap-1" data-testid={`intranet-pinned-${e.id}`}>
                                                        <Pin className="w-2.5 h-2.5" /> Angepinnt{e.pin_until ? ` · bis ${new Date(e.pin_until).toLocaleDateString("de-DE")}` : ""}
                                                    </span>
                                                )}
                                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#FFFBEB] text-[#111827] border border-[#FFCC00]">{deptLabel(e.department)}</span>
                                                {e.owner_label && (
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 border ${e.owner_is_hauptsitz ? "bg-[#111827] text-white border-[#111827]" : "bg-gray-50 text-gray-600 border-gray-300"}`} title={e.owner_label} data-testid={`intranet-owner-${e.id}`}>
                                                        <Building2 className="w-2.5 h-2.5" />
                                                        {e.owner_site_code || e.owner_label}{e.owner_is_hauptsitz ? " · HS" : ""}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-bold text-base mt-1.5 break-words">{e.title}</h3>
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            <button onClick={() => togglePin(e)} className={`p-1.5 ${e.pinned ? "text-[#111827]" : "text-gray-400 hover:text-[#111827]"}`} title={e.pinned ? "Anheftung lösen" : "Anheften"} data-testid={`intranet-pin-${e.id}`}>{e.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</button>
                                            <button onClick={() => setEditing(e)} className="p-1.5 text-gray-400 hover:text-[#111827]" title="Bearbeiten" data-testid={`intranet-edit-${e.id}`}><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => remove(e.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Löschen" data-testid={`intranet-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </div>
                                    {e.info && <p className="text-sm text-gray-600 whitespace-pre-wrap break-words mb-2">{e.info}</p>}
                                    {(e.phones || []).length > 0 && (
                                        <div className="space-y-0.5 mb-2">
                                            {e.phones.map((p, i) => (
                                                <div key={i} className="text-sm flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" /><span className="text-gray-500">{p.label}</span> <a href={`tel:${p.number}`} className="font-bold text-[#111827]">{p.number}</a></div>
                                            ))}
                                        </div>
                                    )}
                                    {(e.links || []).length > 0 && (
                                        <div className="space-y-0.5 mb-2">
                                            {e.links.map((l, i) => (
                                                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-1.5 text-[#111827] hover:underline"><LinkIcon className="w-3.5 h-3.5 text-gray-400" />{l.label || l.url}<ExternalLink className="w-3 h-3" /></a>
                                            ))}
                                        </div>
                                    )}
                                    {(e.downloads || []).length > 0 && (
                                        <div className="space-y-0.5 mb-2">
                                            {e.downloads.map((d, i) => (
                                                <a key={i} href={`${PUBLIC_BASE}${d.url}`} target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-1.5 text-[#111827] hover:underline"><FileText className="w-3.5 h-3.5 text-gray-400" />{d.name}</a>
                                            ))}
                                        </div>
                                    )}
                                    {e.access_notes && (
                                        <div className="mt-2 pt-2 border-t border-gray-100 text-[12px] text-gray-500 whitespace-pre-wrap break-words"><span className="font-bold">Zugänge:</span> {e.access_notes}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {editing && <IntranetEditor entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function IntranetEditor({ entry, onClose, onSaved }) {
    const [form, setForm] = useState({
        department: entry.department || "verkauf",
        title: entry.title || "",
        info: entry.info || "",
        phones: entry.phones || [],
        links: entry.links || [],
        downloads: entry.downloads || [],
        access_notes: entry.access_notes || "",
        pinned: entry.pinned || false,
        pin_until: entry.pin_until || "",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const isEdit = Boolean(entry.id);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const addRow = (k, row) => upd(k, [...form[k], row]);
    const updRow = (k, i, key, val) => upd(k, form[k].map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
    const delRow = (k, i) => upd(k, form[k].filter((_, idx) => idx !== i));

    const uploadFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        setUploading(true);
        for (const f of arr) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`„${f.name}" ist größer als 10 MB.`); continue; }
            try {
                const fd = new FormData(); fd.append("file", f);
                const { data } = await api.post("/intranet/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setForm((prev) => ({ ...prev, downloads: [...prev.downloads, data] }));
            } catch { toast.error(`Upload von „${f.name}" fehlgeschlagen`); }
        }
        setUploading(false);
    };

    const save = async () => {
        if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/intranet/${entry.id}`, form);
            else await api.post("/intranet", form);
            toast.success(isEdit ? "Eintrag aktualisiert" : "Eintrag erstellt");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="intranet-editor-modal">
            <div className="bg-white rounded-sm w-full max-w-xl p-5 max-h-[92vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-black text-lg">{isEdit ? "Eintrag bearbeiten" : "Neuer Intranet-Eintrag"}</h3>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" data-testid="intranet-editor-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Abteilung</label>
                        <select value={form.department} onChange={(e) => upd("department", e.target.value)} className="w-full mt-1 h-10 rounded-sm border border-gray-300 px-2 text-sm" data-testid="intranet-dept-select">
                            {DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Titel</label>
                        <input value={form.title} onChange={(e) => upd("title", e.target.value)} className="w-full mt-1 h-10 rounded-sm border border-gray-300 px-3 text-sm" data-testid="intranet-title-input" />
                    </div>
                </div>

                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Information</label>
                <textarea value={form.info} onChange={(e) => upd("info", e.target.value)} rows={3} className="w-full mt-1 mb-3 rounded-sm border border-gray-300 px-3 py-2 text-sm resize-none" data-testid="intranet-info-input" />

                {/* Rufnummern */}
                <RowEditor title="Rufnummern" rows={form.phones} onAdd={() => addRow("phones", { label: "", number: "" })}
                    render={(p, i) => (
                        <>
                            <input value={p.label} onChange={(e) => updRow("phones", i, "label", e.target.value)} placeholder="Bezeichnung" className="h-9 rounded-sm border border-gray-300 px-2 text-sm w-40 shrink-0" data-testid={`intranet-phone-label-${i}`} />
                            <input value={p.number} onChange={(e) => updRow("phones", i, "number", e.target.value)} placeholder="Nummer" className="h-9 rounded-sm border border-gray-300 px-2 text-sm flex-1 min-w-0" data-testid={`intranet-phone-number-${i}`} />
                        </>
                    )} onDel={(i) => delRow("phones", i)} testid="phone" />

                {/* Links */}
                <RowEditor title="Links" rows={form.links} onAdd={() => addRow("links", { label: "", url: "" })}
                    render={(l, i) => (
                        <>
                            <input value={l.label} onChange={(e) => updRow("links", i, "label", e.target.value)} placeholder="Bezeichnung" className="h-9 rounded-sm border border-gray-300 px-2 text-sm w-40 shrink-0" data-testid={`intranet-link-label-${i}`} />
                            <input value={l.url} onChange={(e) => updRow("links", i, "url", e.target.value)} placeholder="https://…" className="h-9 rounded-sm border border-gray-300 px-2 text-sm flex-1 min-w-0" data-testid={`intranet-link-url-${i}`} />
                        </>
                    )} onDel={(i) => delRow("links", i)} testid="link" />

                {/* Downloads */}
                <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Downloads</label>
                        <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-[#111827] text-white cursor-pointer" data-testid="intranet-upload-label">
                            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Datei
                            <input type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="intranet-upload-input" />
                        </label>
                    </div>
                    {form.downloads.length === 0 ? <p className="text-[11px] text-gray-400">Keine Dateien.</p> : (
                        <ul className="space-y-1">
                            {form.downloads.map((d, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded-sm px-2 py-1.5" data-testid={`intranet-download-${i}`}>
                                    <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <span className="flex-1 truncate">{d.name}</span>
                                    <button onClick={() => delRow("downloads", i)} className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Weitere Zugänge / Notizen</label>
                <textarea value={form.access_notes} onChange={(e) => upd("access_notes", e.target.value)} rows={2} placeholder="z. B. Portal-Logins, interne Hinweise …" className="w-full mt-1 mb-4 rounded-sm border border-gray-300 px-3 py-2 text-sm resize-none" data-testid="intranet-access-input" />

                {/* Anpinnen + Gültigkeit */}
                <div className="mb-4 rounded-sm border border-[#FFCC00] bg-[#FFFBEB] p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.pinned} onChange={(e) => upd("pinned", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="intranet-pin-checkbox" />
                        <span className="text-sm font-bold text-[#111827]">Als wichtig anpinnen (org-weit oben + Dashboard-Banner)</span>
                    </label>
                    {form.pinned && (
                        <div className="mt-2 flex items-center gap-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Gültig bis (optional)</label>
                            <input type="date" value={form.pin_until || ""} onChange={(e) => upd("pin_until", e.target.value)} className="h-9 rounded-sm border border-gray-300 px-2 text-sm" data-testid="intranet-pin-until-input" />
                            {form.pin_until && <button type="button" onClick={() => upd("pin_until", "")} className="text-xs text-gray-500 hover:text-red-600 underline">unbefristet</button>}
                        </div>
                    )}
                    {form.pinned && <p className="mt-1 text-[11px] text-gray-500">Ohne Datum bleibt die Ankündigung dauerhaft angeheftet. Mit Datum verschwindet sie automatisch nach Ablauf aus Banner & Top-Bereich.</p>}
                </div>

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-300 text-sm font-bold">Abbrechen</button>
                    <button onClick={save} disabled={saving} className="h-10 px-4 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="intranet-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Speichern
                    </button>
                </div>
            </div>
        </div>
    );
}

function RowEditor({ title, rows, render, onAdd, onDel, testid }) {
    return (
        <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{title}</label>
                <button type="button" onClick={onAdd} className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-gray-100 hover:bg-gray-200" data-testid={`intranet-add-${testid}`}>+ Hinzufügen</button>
            </div>
            {rows.length === 0 ? <p className="text-[11px] text-gray-400">Keine Einträge.</p> : (
                <div className="space-y-1.5">
                    {rows.map((r, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            {render(r, i)}
                            <button type="button" onClick={() => onDel(i)} className="text-gray-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
